import { computed, type ComputedRef } from 'vue'

import type { OutcomeBucket } from '../components/ActionOutcomeSummary.vue'
import type { RunDetail, RunDetailItem } from '../models/insightAction.types'
import { useInsightActionStore } from '../stores/insightActionStore'

import { useReasonText } from './useReasonText'

import { useI18n } from '@/shared/i18n'

export function useRevertOutcome(
  runId: () => string,
  detail: () => RunDetail | null,
): ComputedRef<{ submitted: number; buckets: OutcomeBucket[] } | null> {
  const { t } = useI18n()
  const store = useInsightActionStore()
  const { itemReason } = useReasonText()

  return computed(() => {
    const submitted = store.revertQueued[runId()]
    const rows = detail()
    if (submitted === undefined || !rows) return null

    const undone = rows.items.filter((item) => item.status === 'reverted')
    const waiting = rows.items.filter(
      (item) => String(item.reasonCode) === 'revert_awaiting_approval',
    )
    const stuck = rows.items.filter(
      (item) =>
        item.status === 'succeeded' &&
        item.reasonCode &&
        String(item.reasonCode) !== 'revert_awaiting_approval',
    )

    const bucket = (
      kind: OutcomeBucket['kind'],
      title: string,
      items: RunDetailItem[],
    ): OutcomeBucket => ({
      kind,
      title,
      count: items.length,
      rows: items.map((item) => ({ rowKey: item.rowKey, label: item.summary || item.rowKey })),
      message: items[0] ? itemReason(item(items[0])) : '',
    })

    return {
      submitted,
      buckets: [
        bucket('applied', t('insightActions.outcome.undone'), undone),
        bucket('waiting', t('insightActions.outcome.waitingToUndo'), waiting),
        bucket('failed', t('insightActions.outcome.leftInPlace'), stuck),
      ],
    }
  })
}

function item(row: RunDetailItem): { errorMessage: string; reasonCode: string } {
  return { errorMessage: row.errorMessage, reasonCode: String(row.reasonCode) }
}
